import { NAV } from "@/constants/testIds";

/**
 * BAYO — pure typographic wordmark.
 * Inspired by editorial magazine mastheads (Vogue, Kinfolk, The Gentlewoman):
 * Cormorant Garamond display serif, generous letterspacing, with a small red
 * accent dot at the end as a nod to the Haitian flag.
 *
 * Variants:
 *  - `inline` (default): used in navbar / footer — single line, compact.
 *  - `stacked`: large editorial version for hero sections (optional `tagline`).
 */
export default function Logo({
  variant = "inline",
  size = "md",
  tagline = false,
  className = "",
}) {
  const sizeMap = {
    sm: { wordmark: "text-xl", dot: "w-1 h-1", spacing: "tracking-[0.18em]" },
    md: { wordmark: "text-2xl", dot: "w-1.5 h-1.5", spacing: "tracking-[0.22em]" },
    lg: { wordmark: "text-5xl sm:text-6xl", dot: "w-2.5 h-2.5", spacing: "tracking-[0.14em]" },
    xl: { wordmark: "text-6xl sm:text-7xl lg:text-8xl", dot: "w-3 h-3 sm:w-4 sm:h-4", spacing: "tracking-[0.10em]" },
  };
  const s = sizeMap[size] || sizeMap.md;

  if (variant === "stacked") {
    return (
      <span
        data-testid={NAV.logo}
        className={`inline-flex flex-col items-start leading-none ${className}`}
      >
        <span className="inline-flex items-end gap-[0.18em]">
          <span className={`font-serif-display ${s.wordmark} ${s.spacing} text-[color:var(--navy)] uppercase font-medium leading-none`}>
            Bayo
          </span>
          <span
            aria-hidden="true"
            className={`${s.dot} rounded-full bg-[color:var(--red)] mb-[0.12em]`}
          />
        </span>
        {tagline && (
          <span className="mt-3 text-[10px] tracking-[0.42em] uppercase text-[color:var(--ink-3)] font-medium">
            Magazine · Recettes
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      data-testid={NAV.logo}
      className={`inline-flex items-end leading-none ${className}`}
    >
      <span className={`font-serif-display ${s.wordmark} ${s.spacing} text-[color:var(--navy)] uppercase font-medium leading-none`}>
        Bayo
      </span>
      <span
        aria-hidden="true"
        className={`${s.dot} ml-[0.18em] mb-[0.12em] rounded-full bg-[color:var(--red)]`}
      />
    </span>
  );
}
